/**
 * Delete Payment Dialog Component
 * Colaiste Lurgan Management System - Payment deletion confirmation
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { useDeletePayment } from '../../hooks/usePayments';
import { AlertTriangle, Loader2, Trash2, X } from 'lucide-react';

/**
 * Format currency in EUR with Irish locale
 */
function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount || 0);
}

export function DeletePaymentDialog({ open, onOpenChange, payment, student }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deletePaymentMutation = useDeletePayment({
    onMutate: () => {
      // Return context with student UUID for cache invalidation
      return { studentUuid: student?.uuid };
    },
    onSuccess: () => {
      // Invalidate payment queries
      queryClient.invalidateQueries(['payments']);
      if (student?.uuid) {
        queryClient.invalidateQueries(['payments', 'student', student.uuid]);
      }

      toast({
        title: t('payments.toast.deleteSuccess'),
        description: t('payments.toast.deleteSuccessDescription'),
        variant: 'success',
      });
      onOpenChange(false);
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        t('payments.toast.deleteError');
      toast({
        title: t('payments.toast.deleteErrorTitle'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const handleDelete = () => {
    if (!payment?.uuid) return;
    deletePaymentMutation.mutate(payment.uuid);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            {t('payments.deletePaymentTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('payments.deletePaymentDescription')}
          </DialogDescription>
        </DialogHeader>

        {/* Payment Details */}
        {payment && (
          <div className="bg-gray-50 border rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('payments.form.paymentType')}:</span>
              <span className="font-medium">
                {t(`payments.type.${payment.payment_type}`)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('payments.form.amount')}:</span>
              <span className="font-medium">{formatCurrency(payment.display_amount)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('payments.form.status')}:</span>
              <span className="font-medium">
                {t(`payments.status.${payment.status}`)}
              </span>
            </div>
            {payment.reference_number && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('payments.form.referenceNumber')}:</span>
                <span className="font-medium">{payment.reference_number}</span>
              </div>
            )}
          </div>
        )}

        {/* Warning */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">
            <strong>{t('common.warning')}:</strong> {t('payments.deleteWarning')}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deletePaymentMutation.isPending}
          >
            <X className="h-4 w-4 mr-2" />
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deletePaymentMutation.isPending}
          >
            {deletePaymentMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('payments.form.deleting')}
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                {t('payments.deletePayment')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeletePaymentDialog;
